import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Row,
  Col,
  Form,
  Input,
  Select,
  DatePicker,
  Cascader,
  Button,
  Space,
  Modal,
} from 'antd';
import moment from 'moment';

import * as Config from '../../../appConfig';
import ActionButtons from './actionButtons';

import getFormattedLocations from '../../../utils/getFormattedLocations';

const EmployeeBasic = ({
  employee,
  designations,
  locations,
  portfolios,
  productTypes,
  stages,
  onBasicInfoUpdate,
  onCancelClick,
  isNew,
  onRelieveEmployeeSubmit,
  onRejoinEmployeeSubmit,
  ability,
}) => {
  const [form] = Form.useForm();

  const [isFormReadOnly, setIsFormReadOnly] = useState(!isNew);
  const [showRelieveModal, setShowRelieveModal] = useState(false);
  // const [aadharTakenError, setAadharTakenError] = useState(false);

  const isSensitiveFieldEditable = (isNew, ability) => {
    return (
      (isNew && ability.toAddSesitiveInfo) ||
      (!isNew && ability.toEditSesitiveInfo)
    );
  };

  return (
    <>
      <Form
        layout="vertical"
        size="large"
        className={`ant-form-custom ${
          isFormReadOnly ? 'ant-form-readOnly' : ''
        }`}
        form={form}
        initialValues={{
          id: employee && employee.id,
          first_name: employee && employee.first_name,
          last_name: employee && employee.last_name,
          designation:
            employee && employee.designation && employee.designation.id,
          location: employee && [
            employee.location.region.id,
            employee.location.id,
          ],
          portfolio: employee && employee.portfolio && employee.portfolio.id,
          productType: employee && employee.productType && employee.productType.id,
          stage: employee && employee.stage && employee.stage.id,
          date_of_joining:
            employee &&
            employee.date_of_joining &&
            moment(employee.date_of_joining),
          date_of_relieving:
            employee &&
            employee.date_of_relieving &&
            moment(employee.date_of_relieving),
          aadhar: employee && employee.aadhar,
          pan: employee && employee.pan,
        }}
        onFinish={(values) => {
          setIsFormReadOnly(!isFormReadOnly);
          if (!isSensitiveFieldEditable(isNew, ability)) {
            values['aadhar'] = null;
            values['pan'] = null;
          }
          onBasicInfoUpdate(values, (response) => {
            const { type } = response;
            if (type === 'validation') {
              setIsFormReadOnly(false);
            }
          });
        }}
      >
        <Row>
          <Col flex="auto" style={{ textAlign: 'right' }}>
            {!isNew && ability.toEditEmployee && (
              <ActionButtons
                showEditBtn={isFormReadOnly}
                onEditClick={() => setIsFormReadOnly(!isFormReadOnly)}
                onCancelClick={() => {
                  setIsFormReadOnly(!isFormReadOnly);
                  form.resetFields();
                }}
              />
            )}
          </Col>
        </Row>
        <Row gutter={128}>
          <Col xs={24} md={12}>
            <Form.Item
              label="First Name"
              name="first_name"
              rules={[{ required: true, message: 'Please input First Name!' }]}
            >
              <Input
                placeholder="First Name"
                disabled={isFormReadOnly}
                maxLength="50"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Last Name"
              name="last_name"
              rules={[{ required: true, message: 'Please input Last Name!' }]}
            >
              <Input
                placeholder="Last Name"
                disabled={isFormReadOnly}
                maxLength="50"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={128}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Designation"
              name="designation"
              rules={[
                { required: true, message: 'Please select Designation!' },
              ]}
            >
              <Select placeholder="Please select" disabled={isFormReadOnly}>
                {designations &&
                  designations.map((designation) => (
                    <Select.Option key={designation.id} value={designation.id}>
                      {designation.name}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Location"
              name="location"
              rules={[{ required: true, message: 'Please select Location!' }]}
            >
              <Cascader
                options={getFormattedLocations(locations)}
                disabled={isFormReadOnly}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={128}>
          <Col xs={24} md={6}>
            <Form.Item
              label="Portfolio"
              name="portfolio"
              rules={[{ required: true, message: 'Please select Portfolio!' }]}
            >
              <Select placeholder="Please select" disabled={isFormReadOnly}>
                {portfolios &&
                  portfolios.map((portfolio) => (
                    <Select.Option key={portfolio.id} value={portfolio.id}>
                      {portfolio.name}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item
              label="Product Type"
              name="productType"
              rules={[{ message: 'Please select Product Type!' }]}
            >
              <Select placeholder="Please select" disabled={isFormReadOnly}>
                {productTypes &&
                  productTypes.map((productType) => (
                    <Select.Option key={productType.id} value={productType.id}>
                      {productType.name} ({productType.code})
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item
              label="Stage"
              name="stage"
              rules={[{ message: 'Please select Stage!' }]}
            >
              <Select placeholder="Please select" disabled={isFormReadOnly}>
                {stages &&
                  stages.map((stage) => (
                    <Select.Option key={stage.id} value={stage.id}>
                      {stage.name}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={128}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Date Of Joining"
              name="date_of_joining"
              rules={[
                { required: true, message: 'Please select Date Of Joining!' },
              ]}
            >
              <DatePicker
                disabled={isFormReadOnly}
                format={Config.DATE_FORMAT_INPUT}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            {!isNew && (
              <Row align="bottom">
                <Col xs="12">
                  <Form.Item
                    label={`Date of ${
                      employee.status === 1 ? 'Relieve' : 'Rejoin'
                    }`}
                    name="date_of_relieving"
                    className="ant-field-readOnly"
                  >
                    <DatePicker
                      disabled={true}
                      format={Config.DATE_FORMAT_INPUT}
                    />
                  </Form.Item>
                </Col>
                <Col xs="12">
                  {ability.toRelieveEmployee && employee.status === 1 && (
                    <Button
                      type="link"
                      style={{ marginBottom: '1rem' }}
                      onClick={() => setShowRelieveModal(true)}
                    >
                      <small>Relieve Employee</small>
                    </Button>
                  )}
                  {ability.toRejoinEmployee && employee.status === 2 && (
                    <Button
                      type="link"
                      style={{ marginBottom: '1rem' }}
                      onClick={() => setShowRelieveModal(true)}
                    >
                      <small>Rejoin Employee</small>
                    </Button>
                  )}
                </Col>
              </Row>
            )}
          </Col>
        </Row>
        <Row gutter={128}>
          <Col xs={24} md={12}>
            <Form.Item
              label="PAN Number"
              name="pan"
              rules={[
                {
                  pattern: /^([a-zA-Z]){5}([0-9]){4}([a-zA-Z]){1}?$/,
                  message: 'Enter valid PAN number!',
                },
              ]}
            >
              <Input
                placeholder="PAN Number"
                disabled={
                  isFormReadOnly || !isSensitiveFieldEditable(isNew, ability)
                }
                maxLength="10"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            {(isNew || ability.toCreateEmployee) && (
              <Form.Item
                label="Aadhar(Last 4 digit only)"
                name="aadhar"
                rules={[
                  {
                    required: true,
                    message: 'Please enter valid input!',
                  },
                  {
                    pattern: /^(?:\d*)$/,
                    message: '',
                  },
                  {
                    pattern: /^[\d]{4}$/,
                    message: 'Please enter valid input!',
                  },
                ]}
              >
                <Input
                  placeholder="Aadhar(Last 4 digit only)"
                  disabled={isFormReadOnly}
                />
              </Form.Item>
            )}
          </Col>
        </Row>
        {isNew && ability.toCreateEmployee && (
          <Row>
            <Col xs={24} align="right">
              <Space>
                <Button type="secondary" size="middle" onClick={onCancelClick}>
                  Cancel
                </Button>
                <Button type="primary" size="middle" htmlType="submit">
                  Add Employee
                </Button>
              </Space>
            </Col>
          </Row>
        )}
        <Form.Item name="id" hidden>
          <Input />
        </Form.Item>
      </Form>
      {showRelieveModal && (
        <Modal
          title={`${employee.status === 1 ? 'Relieve' : 'Rejoin'} Employee`}
          visible={showRelieveModal}
          confirmLoading={false}
          destroyOnClose={true}
          centered
          style={{ maxWidth: '700px' }}
          width="80%"
          footer={null}
          onCancel={() => setShowRelieveModal(false)}
          maskClosable={false}
        >
          <Form
            layout="vertical"
            size="large"
            className="ant-form-custom"
            initialValues={{
              id: employee.id,
            }}
            onFinish={(values) => {
              if (employee.status === 1) {
                onRelieveEmployeeSubmit(values, (updated) => {
                  if (updated.status === 2) {
                    setShowRelieveModal(false);
                  }
                  if (updated.date_of_relieving) {
                    form.setFieldsValue({
                      date_of_relieving: moment(updated.date_of_relieving),
                    });
                  }
                });
              } else {
                onRejoinEmployeeSubmit(values, (updated) => {
                  if (updated.status === 1) {
                    setShowRelieveModal(false);
                  }
                  if (updated.date_of_relieving) {
                    form.setFieldsValue({
                      date_of_relieving: null,
                    });
                  }
                });
              }
            }}
            style={{ marginTop: '0' }}
          >
            <Row>
              <Col xs={24}>
                <Form.Item
                  label={`Date of ${
                    employee.status === 1 ? 'Relieve' : 'Rejoin'
                  }`}
                  name="date_of_relieving"
                  rules={[
                    {
                      required: true,
                      message: 'Please input Date Of Relieve!',
                    },
                  ]}
                >
                  <DatePicker format={Config.DATE_FORMAT_INPUT} />
                </Form.Item>
              </Col>
            </Row>
            {employee.status === 1 && (
              <Row>
                <Col xs={24}>
                  <Form.Item
                    label="Reason"
                    name="reason"
                    rules={[
                      { required: true, message: 'Please input Reason!' },
                    ]}
                  >
                    <Input placeholder="Reason" maxLength="255" />
                  </Form.Item>
                </Col>
              </Row>
            )}
            <Row>
              <Col xs={24} align="right">
                <Space>
                  <Button
                    type="secondary"
                    size="middle"
                    onClick={() => setShowRelieveModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="primary" size="middle" htmlType="submit">
                    {employee.status === 1
                      ? 'Relieve Employee'
                      : 'Rejoin Employee'}
                  </Button>
                </Space>
              </Col>
            </Row>
            <Form.Item name="id" hidden>
              <Input />
            </Form.Item>
          </Form>
        </Modal>
      )}
    </>
  );
};

EmployeeBasic.defaultProps = {
  isNew: false,
  ability: {},
};

EmployeeBasic.propTypes = {
  employee: PropTypes.object,
  designations: PropTypes.array.isRequired,
  locations: PropTypes.array.isRequired,
  isNew: PropTypes.bool.isRequired,
  onBasicInfoUpdate: PropTypes.func,
  onCancelClick: PropTypes.func,
  onRelieveEmployeeSubmit: PropTypes.func,
  ability: PropTypes.object.isRequired,
};

export default EmployeeBasic;
